import React, { useState, useMemo } from 'react';
import axios from 'axios';
// ✅ IMPORT: Using style-supported library for Excel formatting
import * as XLSX from 'xlsx-js-style'; 
import '../assets/css/DataChecker.css'; 

// --- CONFIGURATION ---
const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5002'
  : 'https://bulkup.nectarinfotel.in';
const API_URL = `${API_BASE_URL}/api/validate`;

// ============================================================
// 📘 UP (Uttar Pradesh) VALIDATION RULES — static reference
// Mirrors backend/utils/validation.js. Used to generate the
// "Download UP Rules Reference" workbook so PACS operators can
// see exactly what the DataChecker will enforce BEFORE upload.
// ============================================================
const UP_RULES = {
    sheets: {
        Membership: {
            mandatory: [
                'AdmissionNo', 'CustomerTypeDescription', 'MemberTypeDescription',
                'MemberName', 'DOB', 'Age', 'AdmissionDate', 'GenderDescription',
                'MaritalStatusDesc', 'CommunityDescription', 'CasteDescription',
                'FarmerTypeDescription', 'VillageDescription'
            ],
            allowed: {
                CustomerTypeDescription: ['member', 'nominal member', 'organisation']
            },
            dateFields: ['DOB', 'AdmissionDate', 'GPAIssueDate'],
            integerFields: ['AdmissionNo', 'Age', 'BranchId', 'GroupMemberCount'],
            decimalFields: ['ShareBalance', 'ThriftBalance', 'DividentBalance'],
            businessRules: [
                { id: 'UP-M-1', rule: 'Male member must have Father Name', col: 'FatherName', level: 'ERROR' },
                { id: 'UP-M-2', rule: 'Married Female must have Spouse Name', col: 'SpouseName', level: 'ERROR' },
                { id: 'UP-M-3', rule: 'Unmarried Female must have Father Name', col: 'FatherName', level: 'ERROR' },
                { id: 'UP-M-4', rule: 'B Type member must be Nominal Member', col: 'CustomerTypeDescription', level: 'ERROR' },
                { id: 'UP-M-5', rule: 'AdmissionNo must be unique in file', col: 'AdmissionNo', level: 'ERROR' },
                { id: 'UP-M-6', rule: 'AdmissionNo must NOT already exist in DB', col: 'AdmissionNo', level: 'ERROR' },
                { id: 'UP-M-7', rule: 'Ledger Folio repeats within file (Observation)', col: 'LedgerFolioNo', level: 'WARNING' },
                { id: 'UP-M-8', rule: 'Aadhaar must be exactly 12 digits', col: 'AdhaarCardNo', level: 'ERROR' },
                { id: 'UP-M-9', rule: 'Aadhaar must be unique in file', col: 'AdhaarCardNo', level: 'ERROR' },
                { id: 'UP-M-10', rule: 'ShareBalance = 0 with linked Loan (Observation)', col: 'ShareBalance', level: 'WARNING' },
                { id: 'UP-M-11', rule: 'ShareBalance = 0 with no Loan (Strict Error)', col: 'ShareBalance', level: 'ERROR' }
            ]
        },
        ShortTermLoans: {
            mandatory: [
                'SlNo', 'ProductDescription', 'AdmissionNo', 'LoanNo', 'Scheme',
                'CropDescription', 'SanctionedDate', 'SanctionedAmount', 'LoanPeriod',
                'DueDate', 'OutstandingPrincipal', 'OutstandingInterest'
            ],
            allowed: {
                ProductDescription: ['normal kcc', 'medium term loan', 'long term loan'],
                Scheme: ['sao', 'nabard'],
                CropDescription: ['paddy', 'potato', 'wheat', 'sugarcane', 'others']
            },
            dateFields: ['SanctionedDate', 'DueDate'],
            integerFields: ['SlNo', 'AdmissionNo', 'BranchId'],
            decimalFields: [
                'SanctionedAmount', 'ROIPercentage', 'PenalROIPercentage',
                'IODPercentage', 'OutstandingPrincipal', 'OutstandingInterest'
            ],
            businessRules: [
                { id: 'UP-L-1', rule: 'LoanNo must be unique in file', col: 'LoanNo', level: 'ERROR' },
                { id: 'UP-L-2', rule: 'DueDate must match expected fiscal-year date', col: 'DueDate', level: 'ERROR' },
                { id: 'UP-L-3', rule: 'ProductDescription must be in allowed list', col: 'ProductDescription', level: 'ERROR' },
                { id: 'UP-L-4', rule: 'Scheme must be in allowed list', col: 'Scheme', level: 'ERROR' },
                { id: 'UP-L-5', rule: 'CropDescription must be in allowed list', col: 'CropDescription', level: 'ERROR' }
            ]
        },
        LoanTransaction: {
            mandatory: [
                'AdmissionNo', 'ProductDescription', 'LoanNo', 'TransactionDate',
                'DisbursementAmount', 'BalancePrincipal', 'BalanceInterest'
            ],
            allowed: {},
            dateFields: ['TransactionDate'],
            integerFields: ['AdmissionNo', 'BranchId'],
            decimalFields: [
                'DisbursementAmount', 'OtherChargesDebitAmount', 'CollectedPrincipal',
                'CollectedInterest', 'CollectedPenalInterest', 'CollectedIOD',
                'CollectedOthers', 'BalancePrincipal', 'BalanceInterest',
                'BalancePenalInterest', 'BalanceIOD', 'Charges'
            ],
            businessRules: [
                { id: 'UP-LT-1', rule: 'TransactionDate cannot be earlier than SanctionedDate', col: 'TransactionDate', level: 'ERROR' },
                { id: 'UP-LT-2', rule: 'DisbursementAmount cannot exceed SanctionedAmount (per-row)', col: 'DisbursementAmount', level: 'ERROR' },
                { id: 'UP-LT-3', rule: 'ProductDescription must be in allowed list', col: 'ProductDescription', level: 'ERROR' }
            ]
        },
        LandDetails: {
            mandatory: [
                'AdmissionNo', 'Surveyno', 'Totalextentland', 'Cultivableland',
                'Landundercultivation', 'LandvaluePerAcre', 'DeclaredLandinAcres',
                'DeclaredLandValueAmount', 'FarmcategoryDesc', 'IrrigationtypeDesc',
                'LandtypeDesc', 'SoiltypeDesc', 'StateName', 'DistrictDesc',
                'MandalDesc', 'VillageName'
            ],
            allowed: {
                FarmcategoryDesc: ['oral lessees', 'owner', 'sharecroppers', 'tenant farmers'],
                IrrigationtypeDesc: ['bore well', 'canal', 'not available', 'rain', 'tank'],
                LandtypeDesc: ['wet', 'dry', 'not available'],
                SoiltypeDesc: [
                    'desert soil', 'alluvial soil', 'black soil', 'laterite soil',
                    'mountain soil', 'not available', 'peat soil', 'red soil',
                    'saline soil', 'yellow soil'
                ]
            },
            dateFields: [],
            integerFields: ['AdmissionNo'],
            decimalFields: [
                'Totalextentland', 'Cultivableland', 'Landundercultivation',
                'LandvaluePerAcre', 'DeclaredLandinAcres', 'DeclaredLandValueAmount'
            ],
            businessRules: [
                { id: 'UP-LD-1', rule: 'AdmissionNo must be unique in sheet', col: 'AdmissionNo', level: 'ERROR' },
                { id: 'UP-LD-2', rule: 'DeclaredLandValueAmount ≈ DeclaredLandinAcres × LandvaluePerAcre (±5 tolerance)', col: 'DeclaredLandValueAmount', level: 'ERROR' }
            ]
        },
        Savings: {
            mandatory: [
                'AdmissionNo', 'ProductDescription', 'AccountNo', 'DepositDate',
                'ChequeOption', 'OperationTypeDesc', 'Balance'
            ],
            allowed: {
                OperationTypeDesc: [
                    'single', 'any two jointly', 'both jointly', 'either',
                    'either or survivor', 'former or survivor', 'joint operation',
                    'latter or survivor', 'na', 'others'
                ],
                ChequeOption: ['no', 'yes'],
                ProductDescription: [
                    'saving deposits', 'current deposits',
                    'rythu samraksha sb', 'sahakara lakshmi deposits'
                ]
            },
            dateFields: ['DepositDate'],
            integerFields: ['AdmissionNo', 'RegisterSlNo', 'JointAdmissionNo'],
            decimalFields: ['Balance'],
            businessRules: [
                { id: 'UP-S-1', rule: 'ProductDescription must be in allowed list', col: 'ProductDescription', level: 'ERROR' },
                { id: 'UP-S-2', rule: 'OperationTypeDesc must be in allowed list', col: 'OperationTypeDesc', level: 'ERROR' },
                { id: 'UP-S-3', rule: 'ChequeOption must be in allowed list', col: 'ChequeOption', level: 'ERROR' }
            ]
        }
    },
    commonSchema: {
        label: 'Common Schema Rules (apply to all UP sheets)',
        rules: [
            { id: 'UP-C-1', rule: 'Date fields must parse via upload date rules', cols: 'DATE_FIELDS per sheet', level: 'ERROR' },
            { id: 'UP-C-2', rule: 'Integer fields must be whole numbers (no decimals)', cols: 'INTEGER_FIELDS per sheet', level: 'ERROR' },
            { id: 'UP-C-3', rule: 'Decimal fields must be numeric', cols: 'DECIMAL_FIELDS per sheet', level: 'ERROR' },
            { id: 'UP-C-4', rule: 'Integer/Decimal values cannot be negative', cols: 'All numeric fields', level: 'ERROR' }
        ]
    }
};

function DataChecker() {
    const checkerUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || 'null');
        } catch (error) {
            return null;
        }
    }, []);

    // --- STATES ---
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState("No file selected");
    const [loading, setLoading] = useState(false);
    
    // Report Data & Stats
    const [report, setReport] = useState(null);
    const [dctStats, setDctStats] = useState(null); // Stores DCT-1 Stats from Server
    
    // Filters & View Modes
    const [activeSheet, setActiveSheet] = useState('ALL');
    const [activeError, setActiveError] = useState('ALL');
    // viewMode controls the Tabs: 'ERROR' (Red) or 'WARNING' (Yellow)
    const [viewMode, setViewMode] = useState('ERROR'); 

    // --- FILE SELECTION HANDLER ---
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
            setFileName(e.target.files[0].name);
            // Reset everything on new file
            setReport(null); 
            setDctStats(null);
            setActiveSheet('ALL');
            setActiveError('ALL');
            setViewMode('ERROR');
        }
    };

    // --- MAIN VALIDATION API CALL ---
    const handleValidation = async () => {
        if (!file) {
            alert("⚠️ Please Upload Excel File first!");
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('excelFile', file);
        if (checkerUser?.pacs_id) {
            formData.append('PACS_ID', checkerUser.pacs_id);
        }
        
        // ✅ NAYA LOGIC: Backend ko state bhejna
        formData.append('STATE_NAME', checkerUser?.stateName || 'UP');

        try {
            const res = await axios.post(API_URL, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.success) {
                setReport(res.data.report);
                setDctStats(res.data.dctStats); // ✅ Save Stats for Report
                setActiveSheet('ALL');
                setActiveError('ALL');
                setViewMode('ERROR'); // Default show Strict Errors
            }
        } catch (err) {
            console.error("Validation Error:", err);
            alert(`❌ ${err.response?.data?.message || 'Validation failed. Please check the console.'}`);
        } finally {
            setLoading(false);
        }
    };

    // --- 📊 DOWNLOAD DCT-1 SUMMARY REPORT (With Yellow Header & Table) ---
    const handleDownloadDCT1Report = () => {
        if (!dctStats) return;

        const wb = XLSX.utils.book_new();
        
        // 1. Prepare Data Structure (Rows & Columns)
        const data = [
            ["DCT-1 Data Capture Summary Report", ""], // Row 0: Title
            ["Particular", "Total"],                   // Row 1: Headers
            
            ["1. Membership:", ""],
            ["Total Member captured (Count)", dctStats.Membership.Count],
            ["Total Member captured with Zero Share (Count)", dctStats.Membership.ZeroShareCount],
            ["Total Share Deposit Captured (Rs.)", dctStats.Membership.TotalShare],
            ["Total Aadhar Number Captured (Count)", dctStats.Membership.AadharCount],
            ["Total No. of Village Covered (Count)", dctStats.Membership.VillageCount],
            
            ["2. Short Term Loan:", ""],
            ["Total Loan Member captured (Count)", dctStats.ShortTermLoans.Count],
            ["Total Loan Member captured with zero Principal (Count)", dctStats.ShortTermLoans.ZeroPrinCount],
            ["Total Loan Amount Captured in the Short Term loan(Rs.)", dctStats.ShortTermLoans.TotalAmt],
            ["Total Loan interest Captured in the short Term loan (Rs.)", dctStats.ShortTermLoans.TotalInt],
            ["Total Loan Member captured with zero Interest (Count)", dctStats.ShortTermLoans.ZeroIntCount],

            ["3. Loan Transaction:", ""],
            ["Total Loan Transaction Captured in the loan Transaction sheet(Count)", dctStats.LoanTransaction.Count],
            ["Total Loan Amount Captured in the Loan Transaction Sheet(Rs.)", dctStats.LoanTransaction.TotalAmt],
            ["Total Loan interest Captured in the loan Transaction Sheet (Rs.)", dctStats.LoanTransaction.TotalInt],

            ["4. Land Details:", ""],
            ["Total Land Data Captured (Count)", dctStats.LandDetails.Count],

            ["5. Saving Details:", ""],
            ["Total Saving A/C Data captured (Count)", dctStats.Savings.Count],
            ["Total Saving amount captured in the Saving (Rs.)", dctStats.Savings.TotalAmt],

            // 🆕 Assam-only RecurringDeposit section (appears whenever dctStats carries the block —
            // stateName === 'ASSAM' uploads include this; UP uploads keep the count at 0 and the
            // row stays harmlessly in the report).
            ["6. Recurring Deposit (Assam only):", ""],
            ["Total Recurring Deposit accounts captured (Count)", (dctStats.RecurringDeposit && dctStats.RecurringDeposit.Count) || 0],
            ["Total Recurring Deposit principal captured (Rs.)", (dctStats.RecurringDeposit && dctStats.RecurringDeposit.TotalAmt) || 0]
        ];

        const ws = XLSX.utils.aoa_to_sheet(data);

        // 2. Define Styles
        const thinBorder = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
        
        // Header Style (Yellow Background)
        const headerStyle = { 
            font: { bold: true, sz: 14, color: { rgb: "000000" } }, 
            fill: { fgColor: { rgb: "FFFF00" } }, // 🟨 Yellow Color
            alignment: { horizontal: "center" },
            border: thinBorder
        };

        // Sub-Header Style (Grey Background)
        const subHeaderStyle = {
            font: { bold: true },
            fill: { fgColor: { rgb: "EFEFEF" } },
            alignment: { horizontal: "center" },
            border: thinBorder
        };

        // Section Title Style (Blue Text)
        const sectionStyle = {
            font: { bold: true, color: { rgb: "0000FF" } },
            border: thinBorder
        };

        // Normal Cell Style
        const cellStyle = { border: thinBorder };

        // 3. Apply Styles & Column Widths
        ws['!cols'] = [{ wch: 60 }, { wch: 25 }];
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }]; // Merge Title Row

        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
                if (!ws[cellRef]) continue;

                if (R === 0) ws[cellRef].s = headerStyle; // Top Row
                else if (R === 1) ws[cellRef].s = subHeaderStyle; // Sub Header
                else {
                    // Check if it is a Section Header like "1. Membership:"
                    const val = ws[XLSX.utils.encode_cell({r: R, c: 0})].v;
                    if(typeof val === 'string' && (val.includes(":") || val.match(/^\d+\./))) {
                        ws[cellRef].s = sectionStyle;
                    } else {
                        ws[cellRef].s = cellStyle;
                    }
                }
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, "DCT-1 Summary");
        XLSX.writeFile(wb, "DCT_1_Summary_Report.xlsx");
    };

    // --- 📥 DOWNLOAD FULL LOG (Includes 'Type' Column) ---
    const handleDownloadErrors = () => {
        if (!report || report.errorDetails.length === 0) {
            alert("No findings to download.");
            return;
        }
        const wb = XLSX.utils.book_new();
        
        const errorsBySheet = report.errorDetails.reduce((acc, item) => {
            if (!acc[item.sheet]) acc[item.sheet] = [];
            acc[item.sheet].push(item);
            return acc;
        }, {});

        Object.keys(errorsBySheet).forEach(sheetName => {
            const sheetErrors = errorsBySheet[sheetName].map(item => ({
                Row: item.row,
                AdmissionNo: item.admissionNo || 'N/A',
                Column: item.column,
                // ✅ Adding Type Column
                Type: item.level === 'WARNING' ? 'OBSERVATION' : 'STRICT ERROR', 
                Issue: item.error,
                Details: item.details 
            }));

            const ws = XLSX.utils.json_to_sheet(sheetErrors);
            
            // Set widths
            ws['!cols'] = [{ wch: 10 }, { wch: 18 }, { wch: 25 }, { wch: 15 }, { wch: 40 }, { wch: 50 }];
            
            // Header Yellow Style
            const hStyle = { 
                font: { bold: true }, 
                fill: { fgColor: { rgb: "FFFF00" } }, 
                border: { bottom: {style:'thin'} } 
            };

            if (ws['!ref']) {
                const range = XLSX.utils.decode_range(ws['!ref']);
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const address = XLSX.utils.encode_cell({ r: 0, c: C });
                    if(ws[address]) ws[address].s = hStyle;
                }
            }

            XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
        });

        XLSX.writeFile(wb, "Validation_Findings_Log.xlsx");
    };

    // --- 🧮 FILTER LOGIC (Dynamic based on View Mode) ---
    
    // 1. Filter Data based on Tab (Strict vs Observation)
    const relevantData = useMemo(() => {
        if (!report) return [];
        return report.errorDetails.filter(item => item.level === (viewMode === 'ERROR' ? 'ERROR' : 'WARNING'));
    }, [report, viewMode]);

    // 2. Get Sheet List for Dropdown
    const sheetList = useMemo(() => {
        return ['ALL', ...new Set(relevantData.map(item => item.sheet))];
    }, [relevantData]);

    // 3. Filter by Selected Sheet
    const filteredBySheet = useMemo(() => {
        if (activeSheet === 'ALL') return relevantData;
        return relevantData.filter(item => item.sheet === activeSheet);
    }, [relevantData, activeSheet]);

    // 4. Summary for Error Buttons
    const errorSummary = useMemo(() => {
        const summary = {};
        filteredBySheet.forEach(item => {
            summary[item.error] = (summary[item.error] || 0) + 1;
        });
        return summary;
    }, [filteredBySheet]);

    // 5. Final Rows to Display in Table
    const displayedRows = filteredBySheet.filter(item => 
        activeError === 'ALL' || item.error === activeError
    );

    // --- COUNTS FOR TABS ---
    const errorCount = report ? report.errorDetails.filter(e => e.level === 'ERROR').length : 0;
    const warningCount = report ? report.errorDetails.filter(e => e.level === 'WARNING').length : 0;

    // --- JSON FORMATTER HELPER ---
    const formatDetails = (detailsString) => {
        if (!detailsString) return '-';
        if (typeof detailsString === 'string' && !detailsString.trim().startsWith('{')) return <span className="dc-detail-string">{detailsString}</span>;
        try {
            const obj = JSON.parse(detailsString);
            return Object.entries(obj).map(([key, val]) => (
                <span key={key} className="dc-detail-obj">
                    <strong style={{color: '#495057'}}>{key}:</strong> <span style={{color: '#6c757d'}}>{val}</span>
                </span>
            ));
        } catch (e) { return <span style={{color: '#6c757d'}}>{detailsString}</span>; }
    };

    return (
        <div className="dc-container">
            {/* Navbar */}
            <div className="dc-navbar">
                <div className="dc-content-wrapper">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <div></div>
                        {/* ✅ NAYA: State Indicator Badge */}
                        <div style={{background: '#e0e7ff', color: '#4f46e5', padding: '6px 15px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.9rem'}}>
                            <i className="fa-solid fa-map-location-dot me-2"></i> 
                            State Context: {checkerUser?.stateName || 'UP'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="dc-main-container">
                {/* 📂 Upload Card */}
                <div className="dc-card">
                    <div className="dc-card-body">
                        <h3 className="dc-card-title">
                            <i className="fa-solid fa-upload me-2" style={{color: 'var(--primary)'}}></i>
                            Upload Excel File
                        </h3>
                        <div className="dc-upload-area" onClick={() => document.getElementById('fileInput').click()}>
                            <i className="fa-solid fa-file-excel dc-upload-icon"></i>
                            <div className="dc-upload-text">{fileName === "No file selected" ? "Drag & drop or click to browse" : fileName}</div>
                            <input id="fileInput" type="file" onChange={handleFileChange} accept=".xlsx, .xls" style={{display: 'none'}} />
                        </div>
                        <button className="dc-btn-primary" onClick={handleValidation} disabled={loading || !file}>
                            {loading ? <><i className="fa-solid fa-spinner fa-spin me-2"></i> Validating...</> : <><i className="fa-solid fa-shield-check me-2"></i> Start Validation (V3)</>}
                        </button>
                    </div>
                </div>

                {report && (
                    <div>
                        {/* 📋 DCT-1 REPORT CARD */}
                        <div className="dc-card" style={{marginTop: '20px', borderLeft: '5px solid #0d6efd'}}>
                            <div className="dc-card-body" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px'}}>
                                <div>
                                    <h3 style={{margin: 0, color: '#0d6efd'}}><i className="fa-solid fa-file-invoice me-2"></i> DCT-1 Summary Report</h3>
                                    <p style={{margin: '5px 0 0 0', color: '#6c757d'}}>Download the official formatted summary required for submission.</p>
                                </div>
                                <button className="dc-btn-primary" onClick={handleDownloadDCT1Report} style={{backgroundColor: '#0d6efd', minWidth: '200px'}}>
                                    <i className="fa-solid fa-download me-2"></i> Download DCT-1
                                </button>
                            </div>
                        </div>

                        {/* 🔍 Findings Section */}
                        <div className="dc-card">
                            <div className="dc-card-header">
                                <h3><i className="fa-solid fa-list-check me-2"></i> Validation Findings</h3>
                                <button onClick={handleDownloadErrors} className="dc-btn-warning"><i className="fa-solid fa-download"></i> Download Full Log</button>
                            </div>
                            
                            <div className="dc-card-body">
                                
                                {/* 🔴🟡 TOGGLE BUTTONS (TABS) */}
                                <div style={{display: 'flex', gap: '15px', marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '10px'}}>
                                    {/* Error Tab */}
                                    <button 
                                        onClick={() => { setViewMode('ERROR'); setActiveSheet('ALL'); setActiveError('ALL'); }}
                                        style={{
                                            flex: 1, padding: '12px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                                            backgroundColor: viewMode === 'ERROR' ? '#dc3545' : '#f8f9fa',
                                            color: viewMode === 'ERROR' ? 'white' : '#6c757d',
                                            fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                            transition: 'all 0.2s', boxShadow: viewMode === 'ERROR' ? '0 4px 6px rgba(220, 53, 69, 0.2)' : 'none'
                                        }}
                                    >
                                        <i className="fa-solid fa-circle-xmark"></i>
                                        Strict Errors (Rejects)
                                        <span style={{backgroundColor: 'rgba(255,255,255,0.3)', padding: '2px 8px', borderRadius: '10px', fontSize: '0.8rem'}}>{errorCount}</span>
                                    </button>

                                    {/* Observation Tab */}
                                    <button 
                                        onClick={() => { setViewMode('WARNING'); setActiveSheet('ALL'); setActiveError('ALL'); }}
                                        style={{
                                            flex: 1, padding: '12px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                                            backgroundColor: viewMode === 'WARNING' ? '#ffc107' : '#f8f9fa',
                                            color: viewMode === 'WARNING' ? '#212529' : '#6c757d',
                                            fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                            transition: 'all 0.2s', boxShadow: viewMode === 'WARNING' ? '0 4px 6px rgba(255, 193, 7, 0.2)' : 'none'
                                        }}
                                    >
                                        <i className="fa-solid fa-eye"></i>
                                        Observations (Warnings)
                                        <span style={{backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 8px', borderRadius: '10px', fontSize: '0.8rem'}}>{warningCount}</span>
                                    </button>
                                </div>

                                {/* Filters */}
                                <div className="dc-filter-section">
                                    <div className="dc-filter-group">
                                        <label className="dc-filter-label">Filter by Sheet ({viewMode === 'ERROR' ? 'Errors' : 'Observations'})</label>
                                        <div className="dc-filter-row">
                                            {sheetList.length > 0 ? sheetList.map(sheet => (
                                                <button key={sheet} onClick={() => { setActiveSheet(sheet); setActiveError('ALL'); }} className={`dc-filter-btn sheet-btn ${activeSheet === sheet ? 'active' : ''}`}>{sheet}</button>
                                            )) : <span style={{color:'#999'}}>No findings in this category.</span>}
                                        </div>
                                    </div>
                                    
                                    {sheetList.length > 0 && (
                                        <div className="dc-filter-group">
                                            <label className="dc-filter-label">Filter by Issue</label>
                                            <div className="dc-filter-row">
                                                <button onClick={() => setActiveError('ALL')} className={`dc-filter-btn ${activeError === 'ALL' ? 'active' : ''}`}>Show All</button>
                                                {Object.keys(errorSummary).map(err => (
                                                    <button key={err} onClick={() => setActiveError(err)} className={`dc-filter-btn ${activeError === err ? 'active' : ''}`}>{err} ({errorSummary[err]})</button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Table */}
                                <div className="dc-table-container">
                                    <table className="dc-table">
                                        <thead><tr><th>Sheet</th><th>Row</th><th>Admission No</th><th>Column</th><th>Issue</th><th>Details</th></tr></thead>
                                        <tbody>
                                            {displayedRows.length > 0 ? displayedRows.map((item, index) => (
                                                <tr key={index}>
                                                    <td><span className="dc-sheet-name">{item.sheet}</span></td>
                                                    <td><span className="dc-row-badge">Row {item.row}</span></td>
                                                    <td><span className="dc-col-badge">{item.admissionNo || 'N/A'}</span></td>
                                                    <td><span className="dc-col-badge">{item.column}</span></td>
                                                    <td>
                                                        <span style={{
                                                            color: viewMode === 'WARNING' ? '#856404' : '#dc3545',
                                                            fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px'
                                                        }}>
                                                            <i className={viewMode === 'WARNING' ? "fa-solid fa-eye" : "fa-solid fa-circle-xmark"}></i>
                                                            {item.error}
                                                        </span>
                                                    </td>
                                                    <td>{formatDetails(item.details)}</td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="6" style={{textAlign: 'center', padding: '40px', color: '#6c757d'}}>
                                                        <i className="fa-solid fa-check-circle" style={{fontSize: '3rem', marginBottom: '10px', color: '#28a745'}}></i>
                                                        <div style={{fontSize: '1.2rem'}}>Great! No {viewMode === 'ERROR' ? 'Strict Errors' : 'Observations'} found.</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="dc-footer"><p style={{textAlign:'center', color:'#aaa'}}>© 2026 Data Validator V3</p></div>
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css" />
        </div>
    );
}

export default DataChecker;
